/**
 * FloorChartPage — print-ready bale laydown chart for the floor team.
 *
 * Designed for the operator who feeds the blowroom — most likely a
 * shift worker, possibly a brand-new joiner, possibly with limited
 * literacy. Three design rules:
 *
 *   1. COLOR > NUMBER > TEXT
 *      Each lot gets a high-contrast color. The operator follows
 *      colors first, numbers second, bale codes third. Even if they
 *      can't read the bale code, the color tells them which lot it
 *      came from.
 *
 *   2. ONE BIG ANSWER PER ROW
 *      Each mini-group is one HORIZONTAL strip of 7 colored boxes.
 *      Each box answers exactly one question: "what bale goes here?".
 *      No nested tables, no scrolling within rows.
 *
 *   3. TWO MODES — feed-order primary, supplier pull list secondary
 *      Feed Order = which bale into which position (operator).
 *      Supplier Pull List = which lots to fetch from godown (warehouse).
 *      Both printed on the same A4 sheet.
 *
 * Source: GET /bale-mgmt/laydowns/{ld_id}/floor-chart
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Card, Empty, Segmented, Space, Spin, Table, Tag, Typography,
} from 'antd'
import {
  ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined,
  PrinterOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'

const { Text } = Typography

interface Color { bg: string; fg: string; name: string }

interface Position {
  position: number
  bale_no: string
  mill_lot_no: string
  supplier_lot_ref: string | null
  party_name: string
  color: Color
  lot_letter: string
  mini_group: number
  sub_group: number
  weight_kg: number | null
}

interface MiniGroup {
  mini_no: number
  sub_no: number
  positions: Position[]
  distinct_lots: number
  diversity_ok: boolean
}

interface SubGroup {
  sub_no: number
  mini_groups: MiniGroup[]
  total_positions: number
}

interface SupplierGroup {
  sr: number
  party_name: string
  p_lot_no: string | null
  m_lot_no: string | null
  issue_bales: number
  bale_numbers: string[]
  color: Color
}

interface LotLegendEntry {
  lot_id: string
  mill_lot_no: string
  supplier_lot_ref: string | null
  party_name: string
  bales: number
  color: Color
  letter: string
}

interface FloorChart {
  laydown_id: string
  laydown_no: string
  plan_date: string | null
  blowroom_line_name: string | null
  wall_side_label: string
  count_description: string | null
  mini_group_size: number
  sub_group_size: number
  lot_legend: LotLegendEntry[]
  mini_groups: MiniGroup[]
  sub_groups: SubGroup[]
  supplier_groups: SupplierGroup[]
  waste_section: {
    bales: string[]
    details: { bale_no: string; reason: string }[]
    count: number
    reason_summary: string
  }
  totals: {
    grand_total: number
    waste_total: number
    lot_count: number
    mini_group_count: number
    sub_group_count: number
  }
  generated_at: string
}

// Print-friendly CSS — keeps colors when printing, strips chrome.
const PRINT_STYLES = `
@media print {
  body * { visibility: hidden; }
  #floor-chart-printable, #floor-chart-printable * { visibility: visible; }
  #floor-chart-printable {
    position: absolute; left: 0; top: 0;
    width: 100%; padding: 6mm;
  }
  .no-print { display: none !important; }
  @page { size: A4 landscape; margin: 6mm; }
  .mini-group-strip { page-break-inside: avoid; }
  /* Force background-color printing in Chrome / Edge / Safari */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* On-screen styling matching the printed look */
.feed-position-box {
  border-radius: 6px;
  padding: 8px 4px;
  text-align: center;
  font-weight: 600;
  min-height: 84px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  border: 2px solid rgba(0,0,0,0.08);
}
.feed-position-num {
  font-size: 18px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.feed-position-letter {
  display: inline-block;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: rgba(255,255,255,0.85);
  color: #111;
  font-weight: 800;
  font-size: 14px;
  line-height: 26px;
  margin: 4px 0;
}
.feed-position-bale {
  font-size: 10px;
  font-family: 'Roboto Mono', 'Consolas', monospace;
  word-break: break-all;
  line-height: 1.2;
}
.mini-group-strip {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  margin-bottom: 8px;
  background: #fff;
}
.mini-group-header {
  background: #fafafa;
  padding: 4px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  font-size: 11px;
}
.mini-group-positions {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  padding: 6px;
}
.legend-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(0,0,0,0.06);
}
.legend-letter-circle {
  width: 22px; height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
}
`

function PositionBox({ pos }: { pos: Position }) {
  return (
    <div
      className="feed-position-box"
      style={{ background: pos.color.bg, color: pos.color.fg }}
      title={`${pos.party_name} · ${pos.mill_lot_no}`}
    >
      <div className="feed-position-num">{pos.position}</div>
      <div className="feed-position-letter">{pos.lot_letter}</div>
      <div className="feed-position-bale">{pos.bale_no}</div>
    </div>
  )
}

function MiniGroupStrip({ mg, side }: { mg: MiniGroup; side: 'wall' | 'machine' }) {
  // Machine-side view: mirror the visual L→R order so the operator
  // approaching from the blendomate / bale-opener sees position 1 on
  // their LEFT (which is the wall view's RIGHT). Bale numbers stay
  // correct — feeding order is unchanged, only the printed visual
  // flips. Pad cells fill from the OPPOSITE end so the chart aligns
  // to whichever wall the printout is taped to.
  const positions = side === 'machine'
    ? [...mg.positions].reverse()
    : mg.positions
  const padCount = 7 - mg.positions.length
  const arrow = side === 'machine' ? '←' : '→'

  return (
    <div className="mini-group-strip">
      <div className="mini-group-header">
        <Space size={8}>
          <Tag color="blue" style={{ margin: 0, fontWeight: 700 }}>
            MINI {mg.mini_no}
          </Tag>
          <Tag style={{ margin: 0 }}>SUB {mg.sub_no}</Tag>
          <Text style={{ fontSize: 11 }} type="secondary">
            Feed in this order {arrow}
          </Text>
        </Space>
        <Space size={6}>
          {mg.diversity_ok ? (
            <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
              <CheckCircleOutlined /> {mg.distinct_lots} lots mixed
            </Tag>
          ) : (
            <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>
              <WarningOutlined /> Only {mg.distinct_lots} lot{mg.distinct_lots === 1 ? '' : 's'}
            </Tag>
          )}
        </Space>
      </div>
      <div className="mini-group-positions">
        {/* Machine-side: pad cells go FIRST so the numbered bales hug
            the right edge (= wall edge from the machine's POV). */}
        {side === 'machine' && Array.from({ length: padCount }).map((_, i) => (
          <div
            key={`pad-pre-${i}`}
            style={{ minHeight: 84, border: '1px dashed #f0f0f0', borderRadius: 6 }}
          />
        ))}
        {positions.map(p => <PositionBox key={p.position} pos={p} />)}
        {side === 'wall' && Array.from({ length: padCount }).map((_, i) => (
          <div
            key={`pad-post-${i}`}
            style={{ minHeight: 84, border: '1px dashed #f0f0f0', borderRadius: 6 }}
          />
        ))}
      </div>
    </div>
  )
}

export default function FloorChartPage() {
  const { ld_id } = useParams<{ ld_id: string }>()
  const navigate = useNavigate()
  // Two physical printouts — one taped to the wall behind the laydown,
  // one taped to the blendomate / bale-opener side. Default 'wall'
  // because that's where the operator naturally faces while feeding.
  const [side, setSide] = useState<'wall' | 'machine'>('wall')

  const { data, isLoading, error } = useQuery<FloorChart>({
    queryKey: ['floor-chart', ld_id],
    queryFn: () =>
      apiClient.get(`/bale-mgmt/laydowns/${ld_id}/floor-chart`).then(r => r.data),
    enabled: !!ld_id,
  })

  // Inject the print CSS once.
  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.id = 'floor-chart-print-styles'
    styleEl.innerHTML = PRINT_STYLES
    document.head.appendChild(styleEl)
    return () => {
      document.head.removeChild(styleEl)
    }
  }, [])

  if (isLoading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>
  if (error) return <Alert type="error" message="Failed to load floor chart" description={(error as Error).message} />
  if (!data) return <Empty />

  return (
    <div style={{ padding: 12 }}>
      {/* Top bar — hidden when printing */}
      <div className="no-print" style={{ marginBottom: 12 }}>
        <Space wrap>
          {/* Sprint UX — Back used to use navigate(-1) which silently
              fails when the page is opened in a fresh tab (e.g. via
              the Floor Chart shortcut on a tablet). Always route to a
              known landing page so the button is never dead. */}
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/bale-mgmt/laydown-planning')}
          >
            Back to Laydown Planning
          </Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print {side === 'wall' ? 'Wall-Side' : 'Machine-Side'} Sheet
          </Button>
          {/* Sprint UX — two printouts per laydown, one for the wall
              behind the bales, one for the machine side. Same bale
              numbers + same colors but L→R order is mirrored so the
              operator reads the chart correctly from either side. */}
          <Segmented
            value={side}
            onChange={(v) => setSide(v as 'wall' | 'machine')}
            options={[
              { label: 'Wall Side', value: 'wall' },
              { label: 'Machine Side', value: 'machine' },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip — print BOTH views (one for each side of the laydown). A4 Landscape, Background graphics ON.
          </Text>
        </Space>
      </div>

      {/* The printable region */}
      <div id="floor-chart-printable">
        {/* ── Header ───────────────────────────────────────────────── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
          <tbody>
            <tr>
              <td style={{
                border: '2px solid #000', padding: '6px 10px',
                width: '32%', textAlign: 'center',
                background: side === 'machine' ? '#fff7e6' : '#e6f4ff',
              }}>
                <div style={{ fontSize: 11, color: '#666', fontWeight: 600, letterSpacing: 1 }}>
                  TAPE THIS SHEET ON THE
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: side === 'machine' ? '#d46b08' : '#0958d9' }}>
                  {side === 'machine' ? 'MACHINE SIDE' : 'WALL SIDE'}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                  Feed direction {side === 'machine' ? '←' : '→'}
                </div>
              </td>
              <td style={{
                border: '2px solid #000', padding: '6px 10px',
                textAlign: 'center', fontWeight: 800, fontSize: 18,
              }}>
                BALE LAYDOWN — FEEDING ORDER
              </td>
              <td style={{
                border: '2px solid #000', padding: '6px 10px',
                width: '20%', textAlign: 'center',
              }}>
                <div style={{ fontSize: 10 }}>DATE</div>
                <div style={{ fontSize: 14, fontWeight: 800 }}>
                  {data.plan_date ? dayjs(data.plan_date).format('DD-MM-YYYY') : '—'}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Subheader */}
        <div style={{
          marginBottom: 8, padding: '4px 8px',
          border: '1px solid #888', background: '#fafafa',
          display: 'flex', justifyContent: 'space-between',
          fontSize: 12, flexWrap: 'wrap', gap: 8,
        }}>
          <span><Text strong>Laydown:</Text> <Text code>{data.laydown_no}</Text></span>
          {data.count_description && (
            <span><Text strong>Count:</Text> {data.count_description}</span>
          )}
          {data.blowroom_line_name && (
            <span><Text strong>Line:</Text> {data.blowroom_line_name}</span>
          )}
          <span><Text strong>Total Bales:</Text> {data.totals.grand_total}</span>
          <span><Text strong>Lots:</Text> {data.totals.lot_count}</span>
          <span><Text strong>Mini Groups:</Text> {data.totals.mini_group_count} × {data.mini_group_size}</span>
        </div>

        {/* ── LOT LEGEND (the visual key) ──────────────────────────── */}
        <Card
          size="small"
          style={{ marginBottom: 8 }}
          styles={{ body: { padding: '8px 10px' } }}
        >
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>LOT LEGEND</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              — match the colored letter on each bale to a lot below
            </Text>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.lot_legend.map(l => (
              <div key={l.lot_id} className="legend-chip" style={{ background: '#fff' }}>
                <span
                  className="legend-letter-circle"
                  style={{ background: l.color.bg, color: l.color.fg }}
                >
                  {l.letter}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 700, fontSize: 11 }}>
                    {l.mill_lot_no}{l.supplier_lot_ref ? ` (P-${l.supplier_lot_ref})` : ''}
                  </span>
                  <span style={{ fontSize: 9, color: '#666' }}>
                    {l.party_name} · {l.bales} bale{l.bales === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── FEEDING ORDER (the hero section) ─────────────────────── */}
        <div style={{ marginBottom: 10 }}>
          <Text strong style={{ fontSize: 13 }}>
            <ArrowRightOutlined /> FEEDING ORDER — feed each mini-group top-to-bottom,&nbsp;
            {side === 'machine' ? 'right-to-left (machine-side view)' : 'left-to-right (wall-side view)'}
          </Text>
        </div>

        {data.sub_groups.map((sub) => (
          <div key={sub.sub_no} style={{ marginBottom: 12 }}>
            <div style={{
              padding: '4px 10px',
              background: '#001529', color: '#fff',
              borderRadius: 4, fontWeight: 700, fontSize: 12,
              marginBottom: 4,
            }}>
              SUB GROUP {sub.sub_no} ({sub.total_positions} bales)
            </div>
            {sub.mini_groups.map(mg => (
              <MiniGroupStrip key={mg.mini_no} mg={mg} side={side} />
            ))}
          </div>
        ))}

        {/* ── WASTE / LEFTOVER section ─────────────────────────────── */}
        {data.waste_section.count > 0 && (
          <Card
            size="small"
            style={{ marginTop: 10, borderColor: '#faad14', borderWidth: 2 }}
            title={
              <Space>
                <WarningOutlined style={{ color: '#faad14' }} />
                <Text strong style={{ color: '#faad14' }}>
                  ⚠ {data.waste_section.count} WASTE / LEFTOVER BALES — SET ASIDE, DO NOT FEED
                </Text>
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="bale_no"
              pagination={false}
              dataSource={data.waste_section.details}
              columns={[
                { title: 'Bale No', dataIndex: 'bale_no', width: 160,
                  render: (v: string) => <Text code>{v}</Text> },
                { title: 'Reason', dataIndex: 'reason' },
              ]}
            />
          </Card>
        )}

        {/* ── SUPPLIER PULL LIST (warehouse view) ──────────────────── */}
        <Card
          size="small"
          style={{ marginTop: 10 }}
          styles={{ body: { padding: '8px 10px' } }}
          title={
            <Text strong style={{ fontSize: 12 }}>
              SUPPLIER PULL LIST — for warehouse staff to fetch bales by lot
            </Text>
          }
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px', width: 30 }}>S.R</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px' }}>Party Name</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px', width: 70 }}>P.LOT NO</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px', width: 110 }}>M.LOT NO</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px', width: 70, textAlign: 'right' }}>Bales</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '4px 6px' }}>Bale Numbers</th>
              </tr>
            </thead>
            <tbody>
              {data.supplier_groups.map(g => (
                <tr key={`${g.sr}-${g.m_lot_no}`}>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}>
                    {g.sr}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px' }}>
                    <Space size={6}>
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        background: g.color.bg, borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                      }} />
                      <Text strong style={{ fontSize: 11 }}>{g.party_name}</Text>
                    </Space>
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px' }}>
                    {g.p_lot_no || '—'}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px' }}>
                    <Text code style={{ fontSize: 10 }}>{g.m_lot_no}</Text>
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>
                    {g.issue_bales}
                  </td>
                  <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px', fontSize: 10 }}>
                    {g.bale_numbers.join(' · ')}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#f5f5f5' }}>
                <td colSpan={4} style={{ border: '1px solid #d9d9d9', padding: '4px 6px', textAlign: 'right' }}>
                  <Text strong>GRAND TOTAL</Text>
                </td>
                <td style={{ border: '1px solid #d9d9d9', padding: '4px 6px', textAlign: 'right' }}>
                  <Text strong style={{ fontSize: 13 }}>{data.totals.grand_total}</Text>
                </td>
                <td style={{ border: '1px solid #d9d9d9' }} />
              </tr>
            </tbody>
          </table>
        </Card>

        {/* ── Sign-off block ───────────────────────────────────────── */}
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          marginTop: 10, fontSize: 10,
        }}>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #555', padding: '14px 8px 4px', height: 50 }}>
                <Text type="secondary">Issued by</Text>
              </td>
              <td style={{ border: '1px solid #555', padding: '14px 8px 4px', height: 50 }}>
                <Text type="secondary">Supervisor</Text>
              </td>
              <td style={{ border: '1px solid #555', padding: '14px 8px 4px', height: 50 }}>
                <Text type="secondary">Mixing Operator</Text>
              </td>
              <td style={{ border: '1px solid #555', padding: '14px 8px 4px', height: 50 }}>
                <Text type="secondary">QC</Text>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{
          marginTop: 4, fontSize: 9, color: '#999',
          textAlign: 'right',
        }}>
          Generated: {dayjs(data.generated_at).format('DD-MMM-YYYY HH:mm')} —
          erp.unitechspin.com
        </div>
      </div>
    </div>
  )
}
